import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { colors } from '../styles/colors';
import { MapPin, Wifi } from 'lucide-react';

const CareersPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
    // Intentional: mount-only effect; deps intentionally omitted
  }, []);

  const openRoles = [
    {
      id: 'account-manager',
      title: 'Head of Account Management',
      location: 'Australia',
      type: 'Remote',
      path: '/careers/account-manager'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="pt-24 sm:pt-28 pb-16 sm:pb-20 relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.graphite} 100%)`,
        }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <h1 
            className={`text-4xl sm:text-5xl md:text-6xl font-bold mb-6 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ color: colors.white }}
          >
            Careers at Arizore
          </h1>
          <p 
            className={`text-lg sm:text-xl md:text-2xl leading-relaxed max-w-3xl mx-auto transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ color: colors.white, opacity: 0.8, transitionDelay: '100ms' }}
          >
            Join Arizore as we build infrastructure connecting verified real-world assets to modern financial systems.
          </p>
        </div>
      </section>

      {/* Open Roles Section */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: colors.white }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 
            className={`text-2xl sm:text-3xl font-bold mb-10 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ color: colors.graphite, transitionDelay: '200ms' }}
          >
            Open Roles
          </h2>

          <div className="space-y-4">
            {openRoles.map((role, index) => (
              <Link 
                key={role.id}
                to={role.path}
                className={`block transition-all duration-1000 transform ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
                style={{ transitionDelay: `${300 + index * 100}ms` }}
              >
                <div 
                  className="border rounded-xl p-6 sm:p-8 transition-all duration-300 hover:shadow-lg group"
                  style={{ 
                    borderColor: `${colors.graphite}20`,
                    backgroundColor: colors.white
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h3 
                        className="text-xl sm:text-2xl font-semibold mb-3 group-hover:opacity-80 transition-opacity"
                        style={{ color: colors.graphite }}
                      >
                        {role.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <span 
                          className="flex items-center gap-1.5"
                          style={{ color: colors.graphite, opacity: 0.7 }}
                        >
                          <MapPin size={16} />
                          {role.location}
                        </span>
                        <span 
                          className="flex items-center gap-1.5"
                          style={{ color: colors.graphite, opacity: 0.7 }}
                        >
                          <Wifi size={16} />
                          {role.type}
                        </span>
                      </div>
                    </div>
                    <div 
                      className="flex items-center gap-2 text-sm font-medium px-5 py-2.5 rounded-lg border transition-all duration-300 group-hover:bg-gray-50"
                      style={{ 
                        color: colors.graphite,
                        borderColor: `${colors.graphite}30`
                      }}
                    >
                      View Role
                      <svg 
                        className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* No roles message (hidden when roles exist) */}
          {openRoles.length === 0 && (
            <div 
              className="text-center py-12 border rounded-xl"
              style={{ borderColor: `${colors.graphite}15` }}
            >
              <p style={{ color: colors.graphite, opacity: 0.6 }}>
                No open positions at this time. Check back soon.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Culture Section */}
      <section className="py-16 sm:py-20" style={{ backgroundColor: '#F8F8F8' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 
            className="text-2xl sm:text-3xl font-bold mb-6"
            style={{ color: colors.graphite }}
          >
            Why Arizore?
          </h2>
          <p 
            className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto"
            style={{ color: colors.graphite, opacity: 0.8 }}
          >
            We're building something different — infrastructure that bridges real-world value with modern financial systems. 
            If you're excited by complex problems, meaningful work, and the opportunity to shape a new category, we'd love to hear from you.
          </p>
        </div>
      </section>
    </div>
  );
};

export default CareersPage;
